import { graphql } from "gatsby"
import PropTypes from "prop-types"
import React, { useState } from "react"
import AmbientTorusCanvas from "./AmbientTorusCanvas"
import ContactSalesModal from "components/ContactSalesModal"
import { openModal } from "utils/openModal"
import HubspotFormEmbed from "components/HubspotFormEmbed"
import RichTextContent from "components/RichTextContent"
import YoutubeVideo from "components/YoutubeVideo"
import ImageCarousel from "components/ui/ImageCarousel"
import Button from "components/ui/Button"
import Container from "components/ui/Container"
import Heading from "components/ui/Heading"
import Image from "components/ui/Image"
import Link from "components/Link"

function HeroSection({
  heading,
  richText,
  ctaButtons,
  heroVideo,
  heroCarousel,
  heroImage,
  heroImageLink,
  heroForm,
  backgroundImage,
  animatedBackground,
  fullHeight = false,
}) {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false)
  const contactSourcePage =
    ctaButtons?.find((b) => b.contactModal)?.contactModalSource || ""
  // Allow an explicit line break in the H1 by putting a newline in the heading.
  const headingNode =
    typeof heading === "string" && heading.includes("\n")
      ? heading.split("\n").map((line, i, lines) => (
          <React.Fragment key={line}>
            {line}
            {i < lines.length - 1 && <br />}
          </React.Fragment>
        ))
      : heading
  return (
    <section
      className={`relative flex w-full flex-col justify-between bg-big-stone lg:justify-center ${fullHeight ? "min-h-screen" : ""}`}
    >
      <Container className="relative z-10 pb-10 pt-44 text-white lg:pb-32">
        <div className="flex flex-col gap-x-12 gap-y-10 lg:flex-row lg:items-center">
          <div className="w-full lg:w-1/2">
            <Heading
              level="h1"
              title={headingNode}
              subtitle={<RichTextContent>{richText}</RichTextContent>}
              size="large"
              inverted
            />
            {ctaButtons && (
              <div className="mt-6 flex gap-4">
                {ctaButtons.map(
                  (
                    {
                      buttonLabel,
                      color,
                      url,
                      trackingName,
                      contactModal,
                      newTab,
                    },
                    index
                  ) => (
                    <Button
                      key={index}
                      href={url}
                      color={color}
                      trackClick={trackingName}
                      onClick={
                        contactModal
                          ? openModal(setIsContactModalOpen)
                          : undefined
                      }
                      target={newTab ? "_blank" : undefined}
                      size="medium"
                      nowrap
                    >
                      {buttonLabel}
                    </Button>
                  )
                )}
              </div>
            )}
            {heroForm && (
              <div className="mt-8 w-full max-w-md lg:w-1/2">
                <HubspotFormEmbed
                  formId={heroForm.formId}
                  thankYouMessage={heroForm.thankYouMessage}
                />
              </div>
            )}
          </div>
          {heroVideo && (
            <div className="w-full lg:w-1/2">
              <YoutubeVideo
                className="overflow-hidden rounded-lg"
                url={heroVideo}
              />
            </div>
          )}
          {heroCarousel?.length > 0 && (
            <div className="w-full lg:w-1/2">
              <ImageCarousel images={heroCarousel} />
            </div>
          )}
          {heroImage && (
            <div className="lg:w-1/2">
              {heroImageLink ? (
                <Link href={heroImageLink} openNewTab className="block">
                  <Image
                    className="overflow-hidden rounded-lg object-contain"
                    src={heroImage}
                  />
                </Link>
              ) : (
                <Image
                  className="overflow-hidden rounded-lg object-contain"
                  src={heroImage}
                />
              )}
            </div>
          )}
        </div>
      </Container>
      {backgroundImage && (
        <div className="absolute left-0 top-0 h-full w-full">
          <Image className="h-full w-full" src={backgroundImage} />
        </div>
      )}
      {animatedBackground === "ambient-torus" && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 overflow-hidden"
        >
          <AmbientTorusCanvas className="h-full w-full" />
        </div>
      )}
      <ContactSalesModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        sourcePage={contactSourcePage}
      />
    </section>
  )
}

HeroSection.propTypes = {
  heading: PropTypes.string,
  richText: PropTypes.shape({
    raw: PropTypes.string,
  }),
  ctaButtons: PropTypes.arrayOf(
    PropTypes.shape({
      buttonLabel: PropTypes.string,
      color: PropTypes.string,
      url: PropTypes.string,
      trackingName: PropTypes.string,
      contactModal: PropTypes.bool,
      contactModalSource: PropTypes.string,
    })
  ),
  heroVideo: PropTypes.string,
  heroCarousel: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      caption: PropTypes.string,
    })
  ),
  heroImage: PropTypes.shape({
    url: PropTypes.string,
    description: PropTypes.string,
  }),
  heroImageLink: PropTypes.string,
  heroForm: PropTypes.shape({
    formId: PropTypes.string.isRequired,
    formLabel: PropTypes.string,
    thankYouMessage: PropTypes.string,
  }),
  backgroundImage: PropTypes.shape({
    url: PropTypes.string,
    description: PropTypes.string,
  }),
  /** Optional animated canvas overlay behind the hero content. */
  animatedBackground: PropTypes.oneOf(["ambient-torus"]),
  fullHeight: PropTypes.bool,
}

export default HeroSection

export const query = graphql`
  fragment HeroSection on ContentfulHeroSection {
    id
    __typename
    heading
    richText {
      raw
    }
    ctaButtons {
      buttonLabel
      color
      url
      trackingName
    }
    heroVideo
    heroImage {
      gatsbyImageData(quality: 100, placeholder: BLURRED)
      description
    }
    heroForm {
      formId
    }
    backgroundImage {
      gatsbyImageData(quality: 100, layout: FULL_WIDTH)
      description
    }
  }
`
